/* eslint-disable no-restricted-syntax, max-lines, no-invalid-this */

/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import hasIteratorSymbolSupport from '@stdlib/assert-has-iterator-symbol-support';
import { isPrimitive as isBigInt } from '@stdlib/assert-is-bigint';
import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import { isPrimitive as isIntegerPrimitive } from '@stdlib/assert-is-integer';
import isCollection from '@stdlib/assert-is-collection';
import isArrayBuffer from '@stdlib/assert-is-arraybuffer';
import isObject from '@stdlib/assert-is-object';
import isFunction from '@stdlib/assert-is-function';
import isUint64 from '@stdlib/assert-is-uint64';
import isInteger from '@stdlib/math-base-assert-is-integer';
import ITERATOR_SYMBOL from '@stdlib/symbol-iterator';
import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import setReadOnlyAccessor from '@stdlib/utils-define-nonenumerable-read-only-accessor';
import accessorGetter from '@stdlib/array-base-accessor-getter';
import getter from '@stdlib/array-base-getter';
import Uint32Array from '@stdlib/array-uint32';
import copy from '@stdlib/array-base-copy';
import Number from '@stdlib/number-ctor';
import Uint64 from '@stdlib/number-uint64-ctor';
import { assign as toWords } from '@stdlib/number-uint64-base-to-words';
import format from '@stdlib/error-tools-fmtprodmsg';
import indices from './indices.js';
import fromIterator from './from_iterator.js';
import fromIteratorMap from './from_iterator_map.js';
import fromArray from './from_array.js';


// VARIABLES //

var BYTES_PER_ELEMENT = Uint32Array.BYTES_PER_ELEMENT * 2;
var HAS_ITERATOR_SYMBOL = hasIteratorSymbolSupport();


// FUNCTIONS //

/**
* Returns a boolean indicating if a value is a 64-bit unsigned integer array.
*
* @private
* @param {*} value - value to test
* @returns {boolean} boolean indicating if a value is a unsigned typed array
*/
function isUint64Array( value ) {
	return (
		value instanceof Uint64Array ||
		(
			typeof value === 'object' &&
			value !== null &&
			value.constructor.name === 'Uint64Array' &&
			value.BYTES_PER_ELEMENT === BYTES_PER_ELEMENT &&
			typeof value._length === 'number' && // eslint-disable-line no-underscore-dangle

			// NOTE: we don't perform a more rigorous test here for a typed array for performance reasons, as robustly checking for a typed array instance could require walking the prototype tree and performing relatively expensive constructor checks...
			typeof value._buffer === 'object' // eslint-disable-line no-underscore-dangle
		)
	);
}

/**
* Returns a boolean indicating if a value is a 64-bit unsigned integer typed array constructor.
*
* @private
* @param {*} value - value to test
* @returns {boolean} boolean indicating if a value is a 64-bit unsigned integer typed array constructor
*/
function isUint64ArrayConstructor( value ) {
	return ( value === Uint64Array );
}

/**
* Retrieves a 64-bit unsigned integer from a 32-bit unsigned integer array buffer.
*
* @private
* @param {Uint32Array} buf - array buffer
* @param {NonNegativeInteger} idx - element index
* @returns {Uint64} 64-bit unsigned integer
*/
function getUint64( buf, idx ) {
	idx *= 2;
	return Uint64.of( buf[ idx+indices.HIGH ], buf[ idx+indices.LOW ] );
}


// MAIN //

/**
* 64-bit unsigned integer array constructor.
*
* @constructor
* @param {(NonNegativeInteger|Collection|ArrayBuffer|Iterable)} [arg] - length, typed array, array-like object, buffer, or iterable
* @param {NonNegativeInteger} [byteOffset=0] - byte offset
* @param {NonNegativeInteger} [length] - view length
* @throws {RangeError} ArrayBuffer byte length must be a multiple of `8`
* @throws {TypeError} if provided only a single argument, must provide a valid argument
* @throws {TypeError} byte offset must be a nonnegative integer
* @throws {RangeError} byte offset must be a multiple of `8`
* @throws {TypeError} view length must be a positive multiple of `8`
* @throws {RangeError} must provide sufficient memory to accommodate byte offset and view length requirements
* @throws {TypeError} an array element must be either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer
* @throws {TypeError} an iterator must return either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer
* @returns {Uint64Array} 64-bit unsigned integer array
*
* @example
* var arr = new Uint64Array();
* // returns <Uint64Array>
*
* var len = arr.length;
* // returns 0
*
* @example
* var arr = new Uint64Array( 2 );
* // returns <Uint64Array>[ 0n, 0n ]
*
* var len = arr.length;
* // returns 2
*
* @example
* var arr = new Uint64Array( [ 1, 2 ] );
* // returns <Uint64Array>[ 1n, 2n ]
*
* var len = arr.length;
* // returns 2
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = new Uint64Array( buf );
* // returns <Uint64Array>[ 0n, 0n, 0n, 0n ]
*
* var len = arr.length;
* // returns 4
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = new Uint64Array( buf, 16 );
* // returns <Uint64Array>[ 0n, 0n ]
*
* var len = arr.length;
* // returns 2
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 64 );
* var arr = new Uint64Array( buf, 16, 2 );
* // returns <Uint64Array>[ 0n, 0n ]
*
* var len = arr.length;
* // returns 2
*/
function Uint64Array() {
	var byteOffset;
	var nargs;
	var buf;
	var len;
	var tmp;
	var arg;
	var i;

	nargs = arguments.length;
	if ( !(this instanceof Uint64Array) ) {
		if ( nargs === 0 ) {
			return new Uint64Array();
		}
		if ( nargs === 1 ) {
			return new Uint64Array( arguments[0] );
		}
		if ( nargs === 2 ) {
			return new Uint64Array( arguments[0], arguments[1] );
		}
		return new Uint64Array( arguments[0], arguments[1], arguments[2] );
	}
	// Create the underlying data buffer...
	if ( nargs === 0 ) {
		buf = new Uint32Array( 0 ); // backward-compatibility
	} else if ( nargs === 1 ) {
		arg = arguments[ 0 ];
		if ( isNonNegativeInteger( arg ) ) {
			buf = new Uint32Array( arg*2 );
		} else if ( isCollection( arg ) ) {
			len = arg.length;
			if ( isUint64Array( arg ) ) {
				// Source is already arranged as high/low words, so just copy buffer to buffer:
				buf = new Uint32Array( arg._buffer ); // eslint-disable-line no-underscore-dangle
			} else {
				// Assume that every element represents a single 64-bit unsigned integer:
				buf = fromArray( new Uint32Array( len*2 ), arg );
				if ( buf instanceof Error ) {
					throw buf;
				}
			}
		} else if ( isArrayBuffer( arg ) ) {
			if ( !isInteger( arg.byteLength/BYTES_PER_ELEMENT ) ) {
				throw new RangeError( format( 'nullE9', BYTES_PER_ELEMENT, arg.byteLength ) );
			}
			buf = new Uint32Array( arg );
		} else if ( isObject( arg ) ) {
			buf = arg;
			if ( HAS_ITERATOR_SYMBOL === false ) {
				throw new TypeError( format( 'null29', buf ) );
			}
			if ( !isFunction( buf[ ITERATOR_SYMBOL ] ) ) {
				throw new TypeError( format( 'null2A', buf ) );
			}
			buf = buf[ ITERATOR_SYMBOL ]();
			if ( !isFunction( buf.next ) ) {
				throw new TypeError( format( 'null2A', buf ) );
			}
			buf = fromIterator( buf );
			if ( buf instanceof Error ) {
				throw buf;
			}
			len = buf.length;
			tmp = new Uint32Array( len );
			for ( i = 0; i < len; i += 2 ) {
				tmp[ i+indices.HIGH ] = buf[ i ];
				tmp[ i+indices.LOW ] = buf[ i+1 ];
			}
			buf = tmp;
		} else {
			throw new TypeError( format( 'null2A', arg ) );
		}
	} else {
		buf = arguments[ 0 ];
		if ( !isArrayBuffer( buf ) ) {
			throw new TypeError( format( 'null2B', buf ) );
		}
		byteOffset = arguments[ 1 ];
		if ( !isNonNegativeInteger( byteOffset ) ) {
			throw new TypeError( format( 'null2C', byteOffset ) );
		}
		if ( !isInteger( byteOffset/BYTES_PER_ELEMENT ) ) {
			throw new RangeError( format( 'nullEA', BYTES_PER_ELEMENT, byteOffset ) );
		}
		if ( nargs === 2 ) {
			len = buf.byteLength - byteOffset;
			if ( !isInteger( len/BYTES_PER_ELEMENT ) ) {
				throw new RangeError( format( 'null2E', BYTES_PER_ELEMENT, len ) );
			}
			buf = new Uint32Array( buf, byteOffset );
		} else {
			len = arguments[ 2 ];
			if ( !isNonNegativeInteger( len ) ) {
				throw new TypeError( format( 'null2F', len ) );
			}
			if ( (len*BYTES_PER_ELEMENT) > (buf.byteLength-byteOffset) ) {
				throw new RangeError( format( 'null2G', len*BYTES_PER_ELEMENT ) );
			}
			buf = new Uint32Array( buf, byteOffset, len*2 );
		}
	}
	setReadOnly( this, '_buffer', buf );
	setReadOnly( this, '_length', buf.length/2 );

	return this;
}

/**
* Size (in bytes) of each array element.
*
* @name BYTES_PER_ELEMENT
* @memberof Uint64Array
* @readonly
* @type {PositiveInteger}
* @default 8
*
* @example
* var nbytes = Uint64Array.BYTES_PER_ELEMENT;
* // returns 8
*/
setReadOnly( Uint64Array, 'BYTES_PER_ELEMENT', BYTES_PER_ELEMENT );

/**
* Constructor name.
*
* @name name
* @memberof Uint64Array
* @readonly
* @type {string}
* @default 'Uint64Array'
*
* @example
* var name = Uint64Array.name;
* // returns 'Uint64Array'
*/
setReadOnly( Uint64Array, 'name', 'Uint64Array' );

/**
* Creates a new 64-bit unsigned integer array from an array-like object or an iterable.
*
* @name from
* @memberof Uint64Array
* @type {Function}
* @param {(Collection|Object)} src - array-like object or iterable
* @param {Function} [clbk] - callback to invoke for each source element
* @param {*} [thisArg] - context
* @throws {TypeError} `this` context must be a constructor
* @throws {TypeError} `this` must be a 64-bit unsigned integer array
* @throws {TypeError} first argument must be an array-like object or an iterable
* @throws {TypeError} second argument must be a function
* @throws {TypeError} an array element must be either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer
* @throws {TypeError} an iterator must return either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer
* @throws {TypeError} when provided an iterator, a callback must return either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer
* @returns {Uint64Array} 64-bit unsigned integer array
*
* @example
* var arr = Uint64Array.from( [ 1, 2, 3 ] );
* // returns <Uint64Array>[ 1n, 2n, 3n ]
*
* var len = arr.length;
* // returns 3
*
* @example
* import Uint64 from '@stdlib/number-uint64-ctor';
*
* var arr = Uint64Array.from( [ new Uint64( 1 ) ] );
* // returns <Uint64Array>[ 1n ]
*
* var len = arr.length;
* // returns 1
*
* @example
* import Uint64 from '@stdlib/number-uint64-ctor';
* import add from '@stdlib/number-uint64-base-add';
*
* function clbk( v ) {
*     return add( v, v );
* }
*
* var arr = Uint64Array.from( [ new Uint64( 1 ) ], clbk );
* // returns <Uint64Array>[ 2n ]
*
* var len = arr.length;
* // returns 1
*/
setReadOnly( Uint64Array, 'from', function from( src ) {
	var thisArg;
	var nargs;
	var words;
	var clbk;
	var out;
	var buf;
	var tmp;
	var get;
	var len;
	var v;
	var i;
	var j;
	if ( !isFunction( this ) ) {
		throw new TypeError( format('null01') );
	}
	if ( !isUint64ArrayConstructor( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a 64-bit unsigned integer array.' );
	}
	nargs = arguments.length;
	if ( nargs > 1 ) {
		clbk = arguments[ 1 ];
		if ( !isFunction( clbk ) ) {
			throw new TypeError( format( 'null2H', clbk ) );
		}
		if ( nargs > 2 ) {
			thisArg = arguments[ 2 ];
		}
	}
	if ( isCollection( src ) ) {
		if ( clbk ) {
			len = src.length;
			if ( src.get && src.set ) {
				get = accessorGetter( 'default' );
			} else {
				get = getter( 'default' );
			}
			out = new this( len );
			buf = out._buffer; // eslint-disable-line no-underscore-dangle

			words = [ 0, 0 ];
			j = 0;
			for ( i = 0; i < len; i++ ) {
				v = clbk.call( thisArg, get( src, i ), i );
				if ( !isUint64( v ) ) {
					try {
						v = new Uint64( v );
					} catch ( err ) { // eslint-disable-line no-unused-vars
						throw new TypeError( format( 'invalid argument. A callback must return either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer. Value: `%s`.', v ) );
					}
				}
				toWords( v, words, 1, 0 );
				buf[ j+indices.HIGH ] = words[ 0 ];
				buf[ j+indices.LOW ] = words[ 1 ];
				j += 2; // stride
			}
			return out;
		}
		return new this( src );
	}
	if ( isObject( src ) && HAS_ITERATOR_SYMBOL && isFunction( src[ ITERATOR_SYMBOL ] ) ) { // eslint-disable-line max-len
		buf = src[ ITERATOR_SYMBOL ]();
		if ( !isFunction( buf.next ) ) {
			throw new TypeError( format( 'null2J', src ) );
		}
		if ( clbk ) {
			tmp = fromIteratorMap( buf, clbk, thisArg );
		} else {
			tmp = fromIterator( buf );
		}
		if ( tmp instanceof Error ) {
			throw tmp;
		}
		len = tmp.length / 2;
		out = new this( len );
		buf = out._buffer; // eslint-disable-line no-underscore-dangle
		for ( i = 0; i < tmp.length; i += 2 ) {
			buf[ i+indices.HIGH ] = tmp[ i ];
			buf[ i+indices.LOW ] = tmp[ i+1 ];
		}
		return out;
	}
	throw new TypeError( format( 'null2J', src ) );
});

/**
* Creates a new 64-bit unsigned integer array from a variable number of arguments.
*
* @name of
* @memberof Uint64Array
* @type {Function}
* @param {...*} element - array elements
* @throws {TypeError} `this` context must be a constructor
* @throws {TypeError} `this` must be a 64-bit unsigned integer array
* @returns {Uint64Array} 64-bit unsigned integer array
*
* @example
* var arr = Uint64Array.of( 1, 2, 3, 4 );
* // returns <Uint64Array>[ 1n, 2n, 3n, 4n ]
*
* var len = arr.length;
* // returns 4
*/
setReadOnly( Uint64Array, 'of', function of() {
	var args;
	var i;
	if ( !isFunction( this ) ) {
		throw new TypeError( format('null01') );
	}
	if ( !isUint64ArrayConstructor( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a 64-bit unsigned integer array.' );
	}
	args = [];
	for ( i = 0; i < arguments.length; i++ ) {
		args.push( arguments[ i ] );
	}
	return new this( args );
});

/**
* Returns an array element located at integer position (index) `i`, with support for both nonnegative and negative integer indices.
*
* @name at
* @memberof Uint64Array.prototype
* @type {Function}
* @param {integer} idx - element index
* @throws {TypeError} `this` must be a 64-bit unsigned integer array
* @throws {TypeError} must provide an integer
* @returns {(Uint64|void)} array element
*
* @example
* var arr = new Uint64Array( 10 );
*
* var u = arr.at( 0 );
* // returns <Uint64>[ 0n ]
*
* arr.set( 1, 0 );
* arr.set( 2, 1 );
* arr.set( 9, 9 );
*
* u = arr.at( 0 );
* // returns <Uint64>[ 1n ]
*
* u = arr.at( -1 );
* // returns <Uint64>[ 9n ]
*
* u = arr.at( 100 );
* // returns undefined
*
* u = arr.at( -100 );
* // returns undefined
*/
setReadOnly( Uint64Array.prototype, 'at', function at( idx ) {
	if ( !isUint64Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a 64-bit unsigned integer array.' );
	}
	if ( !isIntegerPrimitive( idx ) ) {
		throw new TypeError( format( 'null8A', idx ) );
	}
	if ( idx < 0 ) {
		idx += this._length;
	}
	if ( idx < 0 || idx >= this._length ) {
		return;
	}
	return getUint64( this._buffer, idx );
});

/**
* Pointer to the underlying data buffer.
*
* @name buffer
* @memberof Uint64Array.prototype
* @readonly
* @type {ArrayBuffer}
*
* @example
* var arr = new Uint64Array( 10 );
*
* var buf = arr.buffer;
* // returns <ArrayBuffer>
*/
setReadOnlyAccessor( Uint64Array.prototype, 'buffer', function get() {
	return this._buffer.buffer;
});

/**
* Size (in bytes) of the array.
*
* @name byteLength
* @memberof Uint64Array.prototype
* @readonly
* @type {NonNegativeInteger}
*
* @example
* var arr = new Uint64Array( 10 );
*
* var byteLength = arr.byteLength;
* // returns 80
*/
setReadOnlyAccessor( Uint64Array.prototype, 'byteLength', function get() {
	return this._buffer.byteLength;
});

/**
* Offset (in bytes) of the array from the start of its underlying `ArrayBuffer`.
*
* @name byteOffset
* @memberof Uint64Array.prototype
* @readonly
* @type {NonNegativeInteger}
*
* @example
* var arr = new Uint64Array( 10 );
*
* var byteOffset = arr.byteOffset;
* // returns 0
*/
setReadOnlyAccessor( Uint64Array.prototype, 'byteOffset', function get() {
	return this._buffer.byteOffset;
});

/**
* Size (in bytes) of each array element.
*
* @name BYTES_PER_ELEMENT
* @memberof Uint64Array.prototype
* @readonly
* @type {PositiveInteger}
* @default 8
*
* @example
* var arr = new Uint64Array( 10 );
*
* var nbytes = arr.BYTES_PER_ELEMENT;
* // returns 8
*/
setReadOnly( Uint64Array.prototype, 'BYTES_PER_ELEMENT', Uint64Array.BYTES_PER_ELEMENT );

/**
* Returns an iterator for iterating over array key-value pairs.
*
* @name entries
* @memberof Uint64Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a 64-bit unsigned integer array
* @returns {Iterator} iterator
*
* @example
* import Uint64 from '@stdlib/number-uint64-ctor';
*
* var arr = [
*     new Uint64( 1 ),
*     new Uint64( 2 ),
*     new Uint64( 3 )
* ];
* arr = new Uint64Array( arr );
*
* // Create an iterator:
* var it = arr.entries();
*
* // Iterate over the key-value pairs...
* var v = it.next().value;
* // returns [ 0, <Uint64>[ 1n ] ]
*
* v = it.next().value;
* // returns [ 1, <Uint64>[ 2n ] ]
*
* v = it.next().value;
* // returns [ 2, <Uint64>[ 3n ] ]
*
* var bool = it.next().done;
* // returns true
*/
setReadOnly( Uint64Array.prototype, 'entries', function entries() {
	var buffer;
	var self;
	var iter;
	var len;
	var FLG;
	var i;
	if ( !isUint64Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a 64-bit unsigned integer array.' );
	}
	self = this;
	buffer = this._buffer;
	len = this._length;

	// Initialize an iteration index:
	i = -1;

	// Create an iterator protocol-compliant object:
	iter = {};
	setReadOnly( iter, 'next', next );
	setReadOnly( iter, 'return', end );

	if ( ITERATOR_SYMBOL ) {
		setReadOnly( iter, ITERATOR_SYMBOL, factory );
	}
	return iter;

	/**
	* Returns an iterator protocol-compliant object containing the next iterated value.
	*
	* @private
	* @returns {Object} iterator protocol-compliant object
	*/
	function next() {
		i += 1;
		if ( FLG || i >= len ) {
			return {
				'done': true
			};
		}
		return {
			'value': [ i, getUint64( buffer, i ) ],
			'done': false
		};
	}

	/**
	* Finishes an iterator.
	*
	* @private
	* @param {*} [value] - value to return
	* @returns {Object} iterator protocol-compliant object
	*/
	function end( value ) {
		FLG = true;
		if ( arguments.length ) {
			return {
				'value': value,
				'done': true
			};
		}
		return {
			'done': true
		};
	}

	/**
	* Returns a new iterator.
	*
	* @private
	* @returns {Iterator} iterator
	*/
	function factory() {
		return self.entries();
	}
});

/**
* Returns an array element.
*
* @name get
* @memberof Uint64Array.prototype
* @type {Function}
* @param {NonNegativeInteger} idx - element index
* @throws {TypeError} `this` must be a 64-bit unsigned integer array
* @throws {TypeError} must provide a nonnegative integer
* @returns {(Uint64|void)} array element
*
* @example
* var arr = new Uint64Array( 10 );
*
* var z = arr.get( 0 );
* // returns <Uint64>[ 0n ]
*
* arr.set( [ 1 ], 0 );
*
* z = arr.get( 0 );
* // returns <Uint64>[ 1n ]
*
* z = arr.get( 100 );
* // returns undefined
*/
setReadOnly( Uint64Array.prototype, 'get', function get( idx ) {
	if ( !isUint64Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a 64-bit unsigned integer array.' );
	}
	if ( !isNonNegativeInteger( idx ) ) {
		throw new TypeError( format( 'null2K', idx ) );
	}
	if ( idx >= this._length ) {
		return;
	}
	return getUint64( this._buffer, idx );
});

/**
* Number of array elements.
*
* @name length
* @memberof Uint64Array.prototype
* @readonly
* @type {NonNegativeInteger}
*
* @example
* var arr = new Uint64Array( 10 );
*
* var len = arr.length;
* // returns 10
*/
setReadOnlyAccessor( Uint64Array.prototype, 'length', function get() {
	return this._length;
});

/**
* Sets an array element.
*
* ## Notes
*
* -   When provided a typed array, we must check whether the source array shares the same buffer as the target array and whether the underlying memory overlaps. In particular, we are concerned with the following scenario:
*
*     ```text
*     buf:                ---------------------
*     src: ---------------------
*     ```
*
*     In the above, as we copy values from `src`, we will overwrite values in the `src` view, resulting in duplicated values copied into the end of `buf`, which is not intended. Hence, to avoid overwriting source values, we must **copy** source values to a temporary array.
*
*     In the other overlapping scenario,
*
*     ```text
*     buf: ---------------------
*     src:                ---------------------
*     ```
*
*     by the time we begin copying into the overlapping region, we are copying from the end of `src`, a non-overlapping region, which means we don't run the risk of copying copied values, rather than the original `src` values as intended.
*
* @name set
* @memberof Uint64Array.prototype
* @type {Function}
* @param {(Collection|Uint64Array|Uint64|bigint|number)} value - value(s)
* @param {NonNegativeInteger} [i=0] - element index at which to start writing values
* @throws {TypeError} `this` must be a 64-bit unsigned integer array
* @throws {TypeError} index argument must be a nonnegative integer
* @throws {RangeError} index argument is out-of-bounds
* @throws {RangeError} target array lacks sufficient storage to accommodate source values
* @throws {TypeError} an array element must be either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer
* @throws {TypeError} first argument must be either a 64-bit unsigned integer, a value which can be converted to a 64-bit unsigned integer, a 64-bit unsigned integer array, or an array-like object containing 64-bit unsigned integers or values which can be converted to a 64-bit unsigned integers
* @returns {void}
*
* @example
* var arr = new Uint64Array( 10 );
*
* var z = arr.get( 0 );
* // returns <Uint64>[ 0n ]
*
* arr.set( 1n, 0 );
*
* z = arr.get( 0 );
* // returns <Uint64>[ 1n ]
*/
setReadOnly( Uint64Array.prototype, 'set', function set( value ) {
	/* eslint-disable no-underscore-dangle */
	var words;
	var sbuf;
	var idx;
	var buf;
	var tmp;
	var N;
	var v;
	var i;
	var j;
	if ( !isUint64Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a 64-bit unsigned integer array.' );
	}
	buf = this._buffer;
	if ( arguments.length > 1 ) {
		idx = arguments[ 1 ];
		if ( !isNonNegativeInteger( idx ) ) {
			throw new TypeError( format( 'null2L', idx ) );
		}
	} else {
		idx = 0;
	}
	words = [ 0, 0 ];
	if ( isUint64( value ) ) {
		if ( idx >= this._length ) {
			throw new RangeError( format( 'null2M', idx ) );
		}
		idx *= 2;
		toWords( value, words, 1, 0 );
		buf[ idx+indices.HIGH ] = words[ 0 ];
		buf[ idx+indices.LOW ] = words[ 1 ];
		return;
	}
	if ( isNonNegativeInteger( value ) || ( isBigInt( value ) && isNonNegativeInteger( Number( value ) ) ) ) {
		if ( idx >= this._length ) {
			throw new RangeError( format( 'null2M', idx ) );
		}
		value = new Uint64( value );
		idx *= 2;
		toWords( value, words, 1, 0 );
		buf[ idx+indices.HIGH ] = words[ 0 ];
		buf[ idx+indices.LOW ] = words[ 1 ];
		return;
	}
	if ( isUint64Array( value ) ) {
		N = value._length;
		if ( idx+N > this._length ) {
			throw new RangeError( format('null03') );
		}
		sbuf = value._buffer;

		// Check for overlapping memory...
		j = buf.byteOffset + (idx*BYTES_PER_ELEMENT);
		if (
			sbuf.buffer === buf.buffer &&
			(
				sbuf.byteOffset < j &&
				sbuf.byteOffset+sbuf.byteLength > j
			)
		) {
			// We need to copy source values...
			tmp = new Uint32Array( sbuf.length );
			for ( i = 0; i < sbuf.length; i++ ) {
				tmp[ i ] = sbuf[ i ];
			}
			sbuf = tmp;
		}
		idx *= 2;
		j = 0;
		for ( i = 0; i < N; i++ ) {
			buf[ idx+indices.HIGH ] = sbuf[ j+indices.HIGH ];
			buf[ idx+indices.LOW ] = sbuf[ j+indices.LOW ];
			idx += 2; // stride
			j += 2; // stride
		}
		return;
	}
	if ( isCollection( value ) ) {
		N = value.length;
		if ( idx+N > this._length ) {
			throw new RangeError( format('null03') );
		}
		sbuf = value;

		// Check for overlapping memory...
		j = buf.byteOffset + (idx*BYTES_PER_ELEMENT);
		if (
			sbuf.buffer === buf.buffer &&
			(
				sbuf.byteOffset < j &&
				sbuf.byteOffset+sbuf.byteLength > j
			)
		) {
			sbuf = copy( sbuf );
		}
		idx *= 2;
		for ( i = 0; i < N; i++ ) {
			v = sbuf[ i ];
			if ( !isUint64( v ) ) {
				try {
					v = new Uint64( v );
				} catch ( err ) { // eslint-disable-line no-unused-vars
					throw new TypeError( format( 'invalid argument. An array element must be either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer. Value: `%s`.', v ) );
				}
			}
			toWords( v, words, 1, 0 );
			buf[ idx+indices.HIGH ] = words[ 0 ];
			buf[ idx+indices.LOW ] = words[ 1 ];
			idx += 2; // stride
		}
		return;
	}
	throw new TypeError( format( 'invalid argument. First argument must be either a 64-bit unsigned integer, a value which can be converted to a 64-bit unsigned integer, a 64-bit unsigned integer array, or an array-like object containing 64-bit unsigned integers or values which can be converted to a 64-bit unsigned integers. Value: `%s`.', value ) );

	/* eslint-enable no-underscore-dangle */
});


// EXPORTS //

export default Uint64Array;
